// The star view and the match details panel, wired to the identity detail.
//
// Lives in `features/` because the pure components take blob URLs: the crops
// sit behind the auth-gated files endpoint, and only a feature may fetch.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CandidateActions } from '@/components/faces/CandidateDecision'
import { candidateOwner } from '@/components/faces/CandidateOwner'
import { facePageNumber, formatScore } from '@/components/faces/format'
import { MatchDetailsPanel } from '@/components/faces/MatchDetailsPanel'
import { type StarCandidate, StarView } from '@/components/faces/StarView'
import { STAR_MAX_CANDIDATES } from '@/components/faces/star-layout'
import { Skeleton } from '@/components/ui/skeleton'
import type { FaceMatchingCandidate } from '@/services/api/face-matching'
import { useAuthedImageUrls } from './hooks/useAuthedImageUrls'

export interface PhotoMatchingStarProps {
  /**
   * The identity's strongest pending candidates, from the star's own query —
   * the table's filter and page must not narrow this list (SC-007).
   */
  candidates: FaceMatchingCandidate[]
  /** The star's query is still loading. */
  loading?: boolean
  /**
   * The identity being matched: its name and main photo for the centre of the
   * star. `photoUrl` is the API path — this feature resolves it, the pure
   * components never fetch.
   */
  identity: { displayName: string; photoUrl: string | null }
  /** Open the document detail overlay on this candidate's face (slice 08). */
  onOpenFace: (faceId: string) => void
  /** Confirm, reject or merge a candidate, and the face in flight; omitted for a viewer. */
  actions?: CandidateActions
  /** Where an identity's page lives, so a candidate's owner links there (ADR-0042). */
  identityHref: (personId: string) => string
}

/**
 * The seven strongest suggestions around the identity's photo and the details
 * panel beside it — under it on a narrow screen. A node or a panel row opens the
 * document detail overlay on that face (slice 08). The star always renders the
 * head of the API's list — suggested matches first, then by similarity — so after
 * a decision the next candidate fills the gap.
 */
export function PhotoMatchingStar({
  candidates,
  loading = false,
  identity,
  onOpenFace,
  actions,
  identityHref,
}: PhotoMatchingStarProps) {
  const { t } = useTranslation()
  const base = 'cases.investigate.entityResolution.photoMatching'
  // The candidate the pointer or the keyboard is on, shared by the star and the
  // panel so the two highlight the same face.
  const [activeFaceId, setActiveFaceId] = useState<string | null>(null)
  // The last candidate opened in the overlay stays marked on the star, so the
  // analyst sees where they came back from.
  const [openFaceId, setOpenFaceId] = useState<string | null>(null)
  const onStar = candidates.slice(0, STAR_MAX_CANDIDATES)

  const { photoUrl, displayName } = identity
  const images = useAuthedImageUrls([photoUrl, ...onStar.map(candidate => candidate.crop_url)])

  const starCandidates = onStar.map(
    (candidate): StarCandidate => ({
      faceId: candidate.face_id,
      suggestedMatch: candidate.suggested_match,
      score: formatScore(candidate.score),
      documentName: candidate.document_name ?? t(`${base}.unknownDocument`),
      pageNumber: facePageNumber(candidate.page, candidate.document_name),
      qualityFlags: candidate.quality_flags,
      imageUrl: candidate.crop_url ? (images[candidate.crop_url] ?? null) : null,
      assignedTo: candidateOwner(candidate, t, identityHref),
    })
  )

  const openFace = (faceId: string) => {
    setOpenFaceId(faceId)
    onOpenFace(faceId)
  }

  return (
    <>
      {loading ? (
        <Skeleton className="mx-auto h-64 w-64 rounded-full" />
      ) : (
        /* Panel beside the star from `lg` up, under it on a narrow screen. */
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
          <StarView
            centerImageUrl={photoUrl ? (images[photoUrl] ?? null) : null}
            centerLabel={displayName}
            candidates={starCandidates}
            selectedFaceId={openFaceId}
            activeFaceId={activeFaceId}
            onActiveChange={setActiveFaceId}
            onSelect={openFace}
          />
          <MatchDetailsPanel
            candidates={starCandidates}
            selectedFaceId={openFaceId}
            activeFaceId={activeFaceId}
            onActiveChange={setActiveFaceId}
            onOpen={openFace}
            actions={actions}
          />
        </div>
      )}
    </>
  )
}
